import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from 'src/app/_services/alert.service';
import { ApiControllerService } from '../api-controller-service';
import { ExportService } from 'src/app/_services/export.service';
import { environment } from '../../../environments/environment';
import { WebhookEditDialogComponent } from './webhook-edit-dialog/webhook-edit-dialog.component';
import { WebhookTestDialogComponent } from './webhook-test-dialog/webhook-test-dialog.component';
import { EnvironmentOpt } from 'src/app/shared/constants/app.constants';
import { parseHttpErrorMessage } from 'src/app/_helpers/http-error.utils';
import { AuthService } from 'src/app/_services/auth.service';
import { FormsModule } from '@angular/forms';
import { Role } from 'src/app/_helpers/auth.constants';

const { maxWaitingTimeForDownload } = environment;

export enum WebhookEventType {
  Sale = 'Sale',
  Refund = 'Refund'
}

export interface WebhookRequest {
  webhooks: Record<WebhookEventType, string>
}

interface WebhookValue {
  id: string;
  merchantId: string;
  merchantName?: string;
  url: string;
  status: string;
  updatedBy: string;
  updatedAt: string;
  version: number;
}

type WebhookResponse = {webhooks :Record<WebhookEventType, WebhookValue>};

@Component({
  selector: 'app-webhook-management',
  standalone: true,
  imports: [CommonModule,FormsModule, WebhookEditDialogComponent, WebhookTestDialogComponent],
  templateUrl: './webhook-management.component.html',
  styleUrls: ['./webhook-management.component.css']
})
export class WebhookManagementComponent implements OnInit {
  constructor(
    private readonly apiControllerService: ApiControllerService,
    private readonly alertService: AlertService,
    private readonly exportService: ExportService,
    private readonly authService: AuthService,
    private readonly sanitizer: DomSanitizer
  ) {}
  EnvironmentOpt = EnvironmentOpt;
  webhookResponse: WebhookResponse = {webhooks : {Sale: {}, Refund: {}}} as WebhookResponse;

  // --- Edit Dialog State ---
  isEditDialogVisible: boolean = false;
  editSaleUrl: string = '';
  editRefundUrl: string = '';
  editDialogMode: 'create' | 'edit' = 'edit';
  editMerchantName: string = '';
  editMerchantId: string = '';

  // --- Test Dialog State ---
  isTestDialogVisible: boolean = false;
  testEventType: string = '';
  testMerchantId: string = '';
  testWebhookUrl: string = '';
  role: string = '';
  Role = Role;
  isLoading: boolean = true;

  // PAY10 admin merchant search
  merchantSearchQuery: string = '';
  isValidatingMerchant: boolean = false;
  merchantError: string = '';
  validatedMerchant: { merchantId: string; merchantName: string } | null = null;
  hasSearched: boolean = false;

  ngOnInit() {
    this.role = this.authService.getRole();
    if (this.role !== Role.PAY10_ADMIN) {
      this.loadWebhooks();
    }
  }

  /** True when admin has not yet validated a merchant. */
  get isAdminGated(): boolean {
    return this.role === Role.PAY10_ADMIN && !this.validatedMerchant;
  }

  /** Whether the current webhookResponse has at least one configured webhook URL. */
  get hasWebhooks(): boolean {
    const webhooks = this.webhookResponse?.webhooks as any;
    return !!(webhooks?.Sale?.url || webhooks?.Refund?.url);
  }

  /**
   * Toolbar (env / History / Export) is shown only when:
   *  - non-admin (always), or
   *  - admin with a validated merchant AND at least one configured webhook.
   */
  get showToolbar(): boolean {
    return this.role !== Role.PAY10_ADMIN || (!!this.validatedMerchant && this.hasWebhooks);
  }

  // --- Admin merchant search handlers ---
  onAdminSearchChange() {
    if (this.validatedMerchant) {
      this.validatedMerchant = null;
      this.webhookResponse = { webhooks: { Sale: {}, Refund: {} } } as WebhookResponse;
    }
    this.merchantError = '';
    if (!(this.merchantSearchQuery || '').trim()) {
      this.hasSearched = false;
    }
  }

  clearMerchantSearch() {
    this.merchantSearchQuery = '';
    this.merchantError = '';
    this.validatedMerchant = null;
    this.hasSearched = false;
    this.webhookResponse = { webhooks: { Sale: {}, Refund: {} } } as WebhookResponse;
  }

  searchMerchant() {
    const query = (this.merchantSearchQuery || '').trim();
    if (!query || this.isValidatingMerchant) return;

    this.isValidatingMerchant = true;
    this.merchantError = '';
    this.validatedMerchant = null;
    this.hasSearched = true;

    this.apiControllerService.findMerchant(query).subscribe({
      next: (resp: any) => {
        this.isValidatingMerchant = false;
        const data = resp?.response?.data ?? resp?.data ?? null;
        if (data && data.merchantId) {
          this.validatedMerchant = { merchantId: data.merchantId, merchantName: data.merchantName };
          this.loadWebhooks();
        } else {
          this.validatedMerchant = null;
          this.merchantError = `No records found for "${query}"`;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isValidatingMerchant = false;
        this.validatedMerchant = null;
        console.error('Error finding merchant:', err);
        this.merchantError = `No records found for "${query}"`;
      }
    });
  }

  private loadWebhooks(onDone?: () => void) {
    const merchantId = this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined;
    this.apiControllerService.getWebhooks(merchantId).subscribe({
      next: (resp: any) => {
        const payload = resp?.response?.data || resp?.data || resp;
        this.webhookResponse = payload;

        // Auto-populate validatedMerchant for admin from webhook data
        if (this.role === Role.PAY10_ADMIN && !this.validatedMerchant) {
          const webhooks = payload?.webhooks as any;
          const sampleWebhook = webhooks?.Sale || webhooks?.Refund;
          if (sampleWebhook?.merchantId && sampleWebhook?.merchantName) {
            this.validatedMerchant = {
              merchantId: sampleWebhook.merchantId,
              merchantName: sampleWebhook.merchantName
            };
          }
        }

        if (onDone) onDone();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error fetching webhooks:', error);
        if(error.status !== 401) {
          this.alertService.customInfoPopup({
            icon: 'assets/img/failure.svg',
            title: 'Webhook Fetching Failed',
            description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
            primaryButton: {
              text: 'OK'
            }}
          );
        }
        if (onDone) onDone();
      }
    });
  }

  get eventTypes(): WebhookEventType[] {
    return Object.values(WebhookEventType);
  }

  private getCurrentEnvironment() {
    return localStorage.getItem('environment');
  }

  get currentEnv(): EnvironmentOpt {
    return this.getCurrentEnvironment() as EnvironmentOpt;
  }

  getWebhooksByEnv(env: EnvironmentOpt): WebhookResponse {
      return this.webhookResponse;
  }

  isEditAndTestAble(env: EnvironmentOpt): boolean {
    return env === this.getCurrentEnvironment();
  }
    

  // --- History Panel State ---
  showHistoryPanel: boolean = false;
  historyItems: any[] = [];
  historyPageNumber: number = 0; // zero-based to match backend
  historyPageSize: number = 20;
  historyTotalItems: number = 0;
  historyLoading: boolean = false;
  historyHasMore: boolean = true;
  @ViewChild('historyScrollContainer') historyScrollContainer!: ElementRef<HTMLDivElement>;

  // Toolbar actions
  exportWebhooks() {
    const filename = `webhooks-${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}`;
    const exportedColumns = [
      { key: 'env', label: 'Environment' },
      { key: 'eventType', label: 'Event Tyoe' },
      { key: 'merchantName', label: 'Merchant Name' },
      { key: 'merchantId', label: 'Merchant ID' },
      { key: 'url', label: 'Webhook' },
      { key: 'status', label: 'Status' },
      { key: 'updatedBy', label: 'Last Update On' },
      { key: 'updatedAt', label: 'Last Update By' }
    ];
    // Build exported data array from the record of webhooks, mapping to exportedColumns' keys
    const exportedData = Object.entries(this.webhookResponse?.webhooks || {}).map(([eventType, webhookValue]) => {
      const webhookData: any = webhookValue as any;
      const env = this.getCurrentEnvironment();
      return {
        env: env,
        eventType: eventType,
        merchantName: webhookData?.merchantName || '',
        merchantId: webhookData?.merchantId || '',
        url: webhookData?.url || '',
        status: webhookData?.status || '',
        updatedBy: webhookData?.updatedBy || '',
        updatedAt: webhookData?.updatedAt || ''
      };
    });
    this.exportService.exportToXLSX(exportedData, filename, 'Sheet1', exportedColumns)
      .then(() => {
        this.showDownloadSuccessfulDialog();
      })
      .catch((error) => {
        console.error('Error exporting webhooks:', error);
        this.showDownloadUnsuccessfulDialog();
      });
  }

  // ========== History Panel Methods ==========
  openHistoryPanel() {
    this.showHistoryPanel = true;
    // reset
    this.historyItems = [];
    this.historyPageNumber = 0;
    this.historyHasMore = true;
    this.historyTotalItems = 0;
    // defer fetch to ensure panel rendered
    setTimeout(() => {
      if (this.historyScrollContainer?.nativeElement) {
        this.historyScrollContainer.nativeElement.scrollTop = 0;
      }
      this.fetchHistoryPage();
    }, 0);
  }

  closeHistoryPanel() {
    this.showHistoryPanel = false;
  }


  onHistoryBackdrop(_ev: MouseEvent) {
    // Close when clicking outside the panel
    this.closeHistoryPanel();
  }

  onKeyDownHistoryPanel(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeHistoryPanel();
    }
  }

  onHistoryScroll() {
    if (!this.historyScrollContainer) return;
    const el = this.historyScrollContainer.nativeElement;
    const threshold = 120; // px from bottom to prefetch next page
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.fetchHistoryPage();
    }
  }

  private fetchHistoryPage() {
    if (this.historyLoading || !this.historyHasMore) return;
    this.historyLoading = true;

    this.apiControllerService
      .getWebhookHistory(this.historyPageSize, this.historyPageNumber, this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined)
      .subscribe({
        next: (resp: any) => {
          const content = resp?.content || resp?.items || [];
          const total = resp?.totalElements ?? resp?.total ?? (this.historyItems.length + content.length);
          this.historyItems = [...this.historyItems, ...content];
          this.historyTotalItems = total;
          this.historyPageNumber += 1;
          this.historyHasMore = this.historyItems.length < total && content.length > 0;
          this.historyLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          console.error('getWebhookHistory failed:', error);
          this.historyLoading = false;
          this.historyHasMore = false;
          this.alertService.customInfoPopup({
            icon: 'assets/img/failure.svg',
            title: 'Webhook History Fetching Failed',
            description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
            primaryButton: {
              text: 'OK'
            }}
          );
        }
      });
  }

  getHistoryTypeLabel(item: any): string {
    return this.getCurrentEnvironment() + " - " + (item?.eventAction || '').toString();
  }

  getHistoryMessageHTML(item: any): SafeHtml {
    const raw: string = (item?.eventMessage || '').toString();
    // Build transformed string without using String.replace, to satisfy lints
    const re = /(^|,\s*)([^→,]+)→/g;
    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(raw)) !== null) {
      const [full, sep, from] = match;
      const start = match.index;
      // Append text before this match
      result += raw.slice(lastIndex, start);
      // Append transformed segment
      const sepOut = sep?.trim().startsWith(',') ? '<br/>' : '';
      // Keep strikethrough for the text before the arrow, remove the arrow and break line
      result += `${sepOut}<s>${from}</s><br/>`;
      lastIndex = start + full.length;
    }
    // Append remaining text
    result += raw.slice(lastIndex);
    return this.sanitizer.bypassSecurityTrustHtml(result);
  }

  formatHistoryTime(item: any): string {
    const dt = item?.eventTime;
    if (!dt) return '';
    try {
      const d = new Date(dt);
      if (!Number.isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} of ${hh}:${min}`;
      }
    } catch {}
    return dt.toString();
  }

  getHistoryActor(item: any): string {
    return item?.modifiedBy || '';
  }

  // ========== Edit Dialog Methods ==========
  openEditDialog(env: EnvironmentOpt) {
    const data = this.getWebhooksByEnv(env)?.webhooks;
    const sale = data?.Sale?.url || '';
    const refund = data?.Refund?.url || '';
    const sampleWebhook = data?.Sale || data?.Refund;
    this.editMerchantName = sampleWebhook?.merchantName || '';
    this.editMerchantId = sampleWebhook?.merchantId || '';
    this.editSaleUrl = sale;
    this.editRefundUrl = refund;
    this.editDialogMode = 'edit';
    this.isEditDialogVisible = true;
  } 

  onEditDialogSuccess() {
    this.loadWebhooks();
  }

  onEditDialogFailed(message: string) {
    if (message) {
      this.alertService.customInfoPopup({
        icon: 'assets/img/failure.svg',
        title: 'Save Failed',
        description: message,
        primaryButton: { text: 'Close' }
      });
    }
  }

  // ========== Test Dialog Methods ==========
  openTestDialog(env: EnvironmentOpt, eventType: WebhookEventType) {
    const webhookData = this.getWebhooksByEnv(env).webhooks?.[eventType];
    this.testEventType = eventType;
    this.testMerchantId = webhookData?.merchantId || '';
    this.testWebhookUrl = webhookData?.url || '';
    this.isTestDialogVisible = true;
  }

  onTestDialogResult(result: { success: boolean; status: number; message: string }) {
    this.alertService.customInfoPopup({
      icon: result.success ? 'assets/img/Tick.svg' : 'assets/img/failure.svg',
      title: result.success ? 'Test Notification Sent' : 'Test Notification Failed',
      description: result.message,
      primaryButton: { text: 'Close' }
    });
  }

  exportHistory() {
    const columns = [
      { key: 'eventAction', label: 'Action' },
      { key: 'eventMessage', label: 'Change' },
      { key: 'eventTime', label: 'DateTime' },
      { key: 'modifiedBy', label: 'Modified By' }
    ];

    this.apiControllerService.exportConfigtHistory('webhook-history', columns, this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined).subscribe({
      next: (resp: any) => {
        if(resp?.fileName) {
          this.pollFileStatus(resp.fileName);
        }
      }, error: (error: HttpErrorResponse) => {
        console.error('Error exporting webhook history:', error);
        this.showDownloadUnsuccessfulDialog();
      }
    });
  }

  // ========== Download helpers ==========
  private pollFileStatus(fileName: string) {
    const maxPollingTime = maxWaitingTimeForDownload;
    const pollingInterval = 3000; // 3 seconds
    const startTime = Date.now();

    const poll = () => {
      if (Date.now() - startTime > maxPollingTime) {
        console.warn('File export polling timeout');
        this.showDownloadUnsuccessfulDialog();
        return;
      }

      this.apiControllerService.checkFileStatusAndGetDownloadLink(fileName).subscribe({
        next: (resp: any) => {
          if (resp?.status === 'ready') {
            this.handleFileDownload(resp);
          } else {
            setTimeout(poll, pollingInterval);
          }
        },
        error: (error: HttpErrorResponse) => {
          if(error.status === 404) {
            setTimeout(poll, pollingInterval);
          } else {
            console.error('Error checking file status:', error);
            this.showDownloadUnsuccessfulDialog();
          }
        }
      });
    };
    poll();
  }

  private handleFileDownload(fileData: any) {
    if (fileData.downloadUrl) {
      try {
        const link = document.createElement('a');
        link.href = fileData.downloadUrl;
        link.download = fileData.fileName || 'export';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => link.remove(), 100);
        this.showDownloadSuccessfulDialog();
      } catch (error) {
        console.error('Error triggering file download:', error);
        this.showDownloadUnsuccessfulDialog();
      }
    } else {
      console.error('No download URL provided');
      this.showDownloadUnsuccessfulDialog();
    }
  }

  private showDownloadSuccessfulDialog(): void {
    this.alertService.customInfoPopup({
      icon: 'assets/img/Tick.svg',
      title: 'Download Started',
      description: 'Your download has been initiated. Please check your browser\'s download folder.',
      primaryButton: { text: 'Close' }
    });
  }

  private showDownloadUnsuccessfulDialog(): void {
    this.alertService.customInfoPopup({
      icon: 'assets/img/failure.svg',
      title: 'Download Unsuccessful',
      description: 'Webhook export failed to download. Try Again.',
      primaryButton: { text: 'Close' }
    });
  }

  addWebhook() {
    this.editSaleUrl = '';
    this.editRefundUrl = '';
    this.editMerchantName = this.validatedMerchant?.merchantName || '';
    this.editMerchantId = this.validatedMerchant?.merchantId || '';
    this.editDialogMode = 'create';
    this.isEditDialogVisible = true;
  }
}
